import { useState, useEffect } from "react";
import { IoSearchOutline } from "react-icons/io5";
import { FaRegStar, FaStar } from "react-icons/fa6";
import { BsEye } from "react-icons/bs";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import {
  getMealDetails,
  clearCurrentMealDetails,
  MealItem,
  RestaurantMealItem,
  MealsPaginationInfo,
} from "../../../redux/mealManagement/mealManagementSlice";
import LoadingSpinner from "../../../components/elements/LoadingSpinner";
import Pagination from "../../../components/Pagination";
import MealDetailsModal from "../../../components/modals/Restaurant/MealDetailsModal";

interface MealTableProps {
  type: "all-meals" | "restaurant-meals";
  meals?: MealItem[];
  restaurantMeals?: RestaurantMealItem[];
  status: "idle" | "loading" | "success" | "failed";
  pagination?: MealsPaginationInfo;
  restaurantId?: string;
  onPageChange?: (page: number) => void;
}

const MealTable = ({
  type,
  meals = [],
  restaurantMeals = [],
  status,
  pagination,
  restaurantId,
  onPageChange,
}: MealTableProps) => {
  const dispatch = useAppDispatch();
  const { currentMealDetails, detailsStatus } = useAppSelector(
    (state: RootState) => state.mealManagement
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [availabilityFilter, setAvailabilityFilter] = useState("all");
  const [selectedMealId, setSelectedMealId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Get data based on type
  const getData = () => {
    return type === "all-meals" ? meals : restaurantMeals;
  };

  // Apply filters to the data (for restaurant meals only, all meals are paginated server-side)
  const getFilteredData = (): (MealItem | RestaurantMealItem)[] => {
    let data = getData();

    if (type === "restaurant-meals") {
      const restaurantData = data as RestaurantMealItem[];
      let filteredData = restaurantData;

      // Apply search filter
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        filteredData = filteredData.filter((item: RestaurantMealItem) =>
          item.name.toLowerCase().includes(searchLower)
        );
      }

      // Apply status filter
      if (statusFilter !== "all") {
        filteredData = filteredData.filter((item: RestaurantMealItem) => item.status === statusFilter);
      }

      return filteredData;
    }

    return data;
  };

  const filteredData = getFilteredData();

  // Pagination for restaurant meals (client-side)
  const totalItems = type === "all-meals" ? (pagination?.totalItems || 0) : filteredData.length;
  const totalPages = type === "all-meals" 
    ? (pagination?.totalPages || 1) 
    : Math.ceil(filteredData.length / itemsPerPage);
  
  const paginatedData = type === "all-meals" 
    ? meals 
    : filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, availabilityFilter]);

  const handleViewDetails = (mealId: string) => {
    setSelectedMealId(mealId);
    dispatch(getMealDetails(mealId));
  };

  const handleCloseModal = () => {
    setSelectedMealId(null);
    dispatch(clearCurrentMealDetails());
  };

  const handlePageChange = (page: number) => {
    if (type === "all-meals" && onPageChange) {
      onPageChange(page);
    } else {
      setCurrentPage(page);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string, isAvailable?: boolean) => {
    if (status === "active") {
      if (isAvailable === false) {
        return "bg-yellow-100 text-yellow-600";
      }
      return "bg-green-100 text-green-600";
    }
    return "bg-red-100 text-red-600";
  };

  const renderRating = (rating: number, totalRatings?: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<FaStar key={i} className="text-yellow-400" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<FaRegStar key={i} className="text-yellow-400" />);
      } else {
        stars.push(<FaRegStar key={i} className="text-gray-300" />);
      }
    }

    return (
      <div className="flex items-center gap-1">
        <div className="flex">{stars}</div>
        <span className="text-sm text-gray-600">
          {rating.toFixed(1)} {totalRatings ? `(${totalRatings})` : ''}
        </span>
      </div>
    );
  };

  const renderTableHeader = () => (
    <>
      <th className="p-3 font-medium text-left">Meal</th>
      <th className="p-3 font-medium text-left">Price</th>
      <th className="p-3 font-medium text-left">Orders</th>
      <th className="p-3 font-medium text-left">Rating</th>
      <th className="p-3 font-medium text-left">Status</th>
      {type === "all-meals" && <th className="p-3 font-medium text-left">Updated</th>}
      <th className="p-3 font-medium text-left">Action</th>
    </>
  );

  const renderAllMealsRow = (meal: MealItem) => (
    <tr key={meal._id} className="border text-sm hover:bg-gray-50">
      <td className="p-5">
        <div className="flex items-center gap-3">
          {meal.image && (
            <img
              src={meal.image}
              alt={meal.name}
              className="w-12 h-12 object-cover rounded-md"
              onError={(e) => {
                (e.target as HTMLImageElement).src = "/assets/placeholder-meal.svg";
              }}
            />
          )}
          <div>
            <p className="font-medium">{meal.name}</p>
            {meal.isDiscount && (
              <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                {meal.discountAmount}% OFF
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="p-5">{formatCurrency(meal.price)}</td>
      <td className="p-5">{meal.ordersCount}</td>
      <td className="p-5">{renderRating(meal.rating, meal.totalRatings)}</td>
      <td className="p-5">
        <span
          className={`${getStatusColor(meal.status, meal.isAvailable)} 
            text-center rounded-md px-2 py-1 text-xs font-medium capitalize`}
        >
          {meal.isAvailable ? 'Available' : 'Unavailable'}
        </span>
      </td>
      <td className="p-5 text-xs text-gray-500">
        {formatDate(meal.updatedAt)}
      </td>
      <td className="p-5">
        <button
          className="text-secondary border border-secondary rounded-md px-3 py-1 flex items-center gap-1 text-xs hover:bg-secondary hover:text-white transition-colors"
          onClick={() => handleViewDetails(meal._id)}
          disabled={detailsStatus === "loading" && selectedMealId === meal._id}
        >
          <BsEye />
          {detailsStatus === "loading" && selectedMealId === meal._id
            ? "Loading..."
            : "View"}
        </button>
      </td>
    </tr>
  );

  const renderRestaurantMealsRow = (meal: RestaurantMealItem) => (
    <tr key={meal.id} className="border text-sm hover:bg-gray-50">
      <td className="p-5">
        <p className="font-medium">{meal.name}</p>
      </td>
      <td className="p-5">{formatCurrency(meal.price)}</td>
      <td className="p-5">{meal.orders}</td>
      <td className="p-5">{renderRating(meal.rating)}</td>
      <td className="p-5">
        <span
          className={`${getStatusColor(meal.status)} 
            text-center rounded-md px-2 py-1 text-xs font-medium capitalize`}
        >
          {meal.status}
        </span>
      </td>
      <td className="p-5">
        <button
          className="text-secondary border border-secondary rounded-md px-3 py-1 flex items-center gap-1 text-xs hover:bg-secondary hover:text-white transition-colors"
          onClick={() => handleViewDetails(meal.id)}
          disabled={detailsStatus === "loading" && selectedMealId === meal.id}
        >
          <BsEye />
          {detailsStatus === "loading" && selectedMealId === meal.id
            ? "Loading..."
            : "View"}
        </button>
      </td>
    </tr>
  );

  return (
    <div className="w-full">
      {/* Search and Filter Section - Only for restaurant meals */}
      {type === "restaurant-meals" && (
        <div className="my-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between bg-white p-4 rounded-md">
          {/* Search Input */}
          <div className="relative flex-1 max-w-md">
            <IoSearchOutline className="w-5 h-5 absolute top-[0.6rem] left-3 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 pl-10 text-sm w-full focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary"
              placeholder="Search meals..."
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Clear Filters Button */}
          {(searchTerm || statusFilter !== "all") && (
            <button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
              }}
              className="text-sm text-secondary hover:text-secondary-dark underline"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="min-h-[500px] bg-white rounded-md">
        <table className="w-full">
          <thead className="font-normal">
            <tr className="bg-gray-100 text-left">{renderTableHeader()}</tr>
          </thead>
          <tbody>
            {status === "loading" ? (
              <tr>
                <td colSpan={7} className="text-center py-8">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : paginatedData.length > 0 ? (
              paginatedData.map((meal) =>
                type === "all-meals"
                  ? renderAllMealsRow(meal as MealItem)
                  : renderRestaurantMealsRow(meal as RestaurantMealItem)
              )
            ) : (
              <tr>
                <td colSpan={7} className="text-center py-8 text-gray-500">
                  {searchTerm || statusFilter !== "all"
                    ? "No meals match your filters."
                    : "No meals found."}
                  {(searchTerm || statusFilter !== "all") && (
                    <div className="mt-2">
                      <button
                        onClick={() => {
                          setSearchTerm("");
                          setStatusFilter("all");
                        }}
                        className="text-secondary hover:underline text-sm"
                      >
                        Clear filters to see all meals
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            itemsPerPage={type === "all-meals" ? (pagination?.itemsPerPage || 10) : itemsPerPage}
            length={totalItems}
            currentPage={type === "all-meals" ? (pagination?.currentPage || 1) : currentPage}
            handlePagination={handlePageChange}
            prevPage={() => handlePageChange(Math.max(1, (type === "all-meals" ? (pagination?.currentPage || 1) : currentPage) - 1))}
            nextPage={() => handlePageChange(Math.min(totalPages, (type === "all-meals" ? (pagination?.currentPage || 1) : currentPage) + 1))}
          />
        </div>
      )}

      {/* Meal Details Modal */}
      {selectedMealId && currentMealDetails && (
        <MealDetailsModal
          meal={currentMealDetails}
          isOpen={true}
          onClose={handleCloseModal}
          isLoading={detailsStatus === "loading"}
        />
      )}
    </div>
  );
};

export default MealTable;